import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthGuard, AdminGuard, SellerGuard, SellerOrAdminGuard } from '@/components/auth/AuthGuard';
import Guarded from '@/components/auth/Guarded';
import SecurityConfig from '@/components/security/SecurityConfig';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';
import { TooltipProvider } from '@/components/ui/tooltip';
// Defer non-critical providers/widgets — keeps them out of the entry chunk
const AnalyticsProvider = lazy(() =>
  import('@/components/analytics/AnalyticsProvider').then((m) => ({ default: m.AnalyticsProvider }))
);
import { Toaster } from '@/components/ui/sonner';
const FloatingChatButton = lazy(() => import('@/components/FloatingChatButton'));
import ErrorBoundary from '@/components/ErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop';
import PageLoader from '@/components/PageLoader';

import Index from '@/pages/Index';
// Lazy-loaded heavy pages
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));

const BookDetails = lazy(() => import('@/pages/BookDetails'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const OrderDetails = lazy(() => import('@/pages/OrderDetails'));

// Lazy-load most pages to keep initial JS small
const Login = lazy(() => import('@/pages/Login'));
const Signup = lazy(() => import('@/pages/Signup'));
const SellBook = lazy(() => import('@/pages/SellBook'));
const EditBook = lazy(() => import('@/pages/EditBook'));
const Cart = lazy(() => import('@/pages/Cart'));
const OrderConfirmation = lazy(() => import('@/pages/OrderConfirmation'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const AllBooks = lazy(() => import('@/pages/AllBooks'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const SellerGuide = lazy(() => import('@/pages/SellerGuide'));
const ShippingManagement = lazy(() => import('@/pages/ShippingManagement'));

const Support = lazy(() => import('@/pages/Support'));
const BuyerUpgrade = lazy(() => import('@/pages/BuyerUpgrade'));
const SellerSignup = lazy(() => import('@/pages/SellerSignup'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const TermsAndConditions = lazy(() => import('@/pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const ReportContent = lazy(() => import('@/pages/ReportContent'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const DeleteData = lazy(() => import('@/pages/DeleteData'));
const SellerSettings = lazy(() => import('@/pages/SellerSettings'));
const SellerProfile = lazy(() => import('@/pages/SellerProfile'));
const AccountSettings = lazy(() => import('@/pages/AccountSettings'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const AppContent = () => {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ==================== PUBLIC ROUTES ==================== */}
          {/* Authentication */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/seller-signup" element={<SellerSignup />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Landing & Browse */}
          <Route path="/" element={<Index />} />
          <Route path="/books" element={<AllBooks />} />
          <Route path="/book/:id" element={<ErrorBoundary><BookDetails /></ErrorBoundary>} />
          <Route path="/category/:category" element={<CategoryPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/seller/:id" element={<SellerProfile />} />

          {/* Static Info Pages */}
          <Route path="/seller-guide" element={<SellerGuide />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy/delete-data" element={<DeleteData />} />
          <Route path="/report-content" element={<ReportContent />} />
          <Route path="/support" element={<Support />} />

          {/* ==================== BUYER ROUTES (Auth Required) ==================== */}
          <Route path="/dashboard" element={<Guarded guard={AuthGuard} guardProps={{}}><Dashboard /></Guarded>} />
          <Route path="/checkout" element={<Guarded guard={AuthGuard} guardProps={{}}><Checkout /></Guarded>} />
          <Route path="/order-confirmation/:id" element={<Guarded guard={AuthGuard} guardProps={{}}><OrderConfirmation /></Guarded>} />
          
          <Route path="/order/:id" element={<Guarded guard={AuthGuard} guardProps={{}}><OrderDetails /></Guarded>} />
          <Route path="/buyer-upgrade" element={<Guarded guard={AuthGuard} guardProps={{}}><BuyerUpgrade /></Guarded>} />
          <Route path="/account" element={<Guarded guard={AuthGuard} guardProps={{}}><AccountSettings /></Guarded>} />

          {/* ==================== SELLER ROUTES ==================== */}
          <Route path="/sell" element={<Guarded guard={SellerGuard} guardProps={{}}><SellBook /></Guarded>} />
          <Route path="/edit-book/:id" element={<Guarded guard={SellerGuard} guardProps={{}}><EditBook /></Guarded>} />
          <Route path="/shipping" element={<Guarded guard={AdminGuard} guardProps={{}}><ShippingManagement /></Guarded>} />
          <Route path="/seller-settings" element={<Guarded guard={SellerGuard} guardProps={{}}><SellerSettings /></Guarded>} />

          {/* ==================== ADMIN ROUTES ==================== */}
          <Route path="/admin" element={<Guarded guard={AdminGuard} guardProps={{}}><AdminPanel /></Guarded>} />

          {/* ==================== FALLBACK ==================== */}
          <Route path="*" element={<ErrorBoundary><NotFound /></ErrorBoundary>} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <FloatingChatButton />
      </Suspense>
    </>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <SecurityConfig>
            <BrowserRouter>
              <TooltipProvider>
                {/* Render the app immediately; analytics + toast UI hydrate
                    in the background without blocking AppContent. */}
                <Suspense fallback={null}>
                  <AnalyticsProvider googleAnalyticsId={import.meta.env.VITE_GA_ID} />
                </Suspense>
                <Suspense fallback={null}>
                  <Toaster />
                </Suspense>
                <AppContent />
              </TooltipProvider>
            </BrowserRouter>
          </SecurityConfig>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
